/**
 * Map Wrapper
 *
 * The Map Wrapper module is mainly responsible for rendering a map.
 * This module contains the following features:
 *
 * * Load styles:
 *   Will load the styles passed in the options.theme when rendering the map.
 *   If there are traffic flow options, the styles loader module will also load them, displaying both on the map.
 *
 * * Copyrights:
 *   We make use of the TomTomAttributionControl module to request the copyrights by using our services.
 *   We then display them on the map.
 *
 * * Request URL and Analytics:
 *   In order to use the TomTom URL and the necessary headers to request the map tiles, we transform the request
 *   (transformRequestFactory) to obtain the correct URL and set the headers with the cors headers used in tomtom.
 *
 */
import mapboxGlRTLText from 'raw-loader!mapbox-gl-rtl-text';
import {isNil} from 'lodash';
import initializeMapOptions from './initializeMapOptions';
import initializeCopyrights from './initializeCopyrights';
import transformRequestFactory from './transformRequestFactory';
import {mapboxMapFactory, appendMapboxProperties} from 'Map/map/mapFactory';
import converters from 'Services/services/converters';
import validators from 'Services/services/validators';
import tiersContainer from 'Map/tier/tiersContainer';
import extendedMapFactory from 'Map/map/extendedMap';
import CUSTOM_EVENTS from 'Map/utils/events/customEvents';
import sourceValidationConfigurer from 'Map/map/sourceValidation/sourceValidationConfigurer';
import sessionId from './sessionId';

function convertAndValidateLanguage(languageOption) {
    return converters.language(languageOption, 'maps');
}

const geopoliticalViewValidator = validators.oneOfValue(['Unified', 'IL', 'IN', 'MA', 'PK', 'AR', 'Arabic', 'RU', 'TR'],
    'geopoliticalView');

function convertAndValidateGeopoliticalView(geopoliticalViewOption) {
    if (isNil(geopoliticalViewOption) || geopoliticalViewOption === '') {
        return geopoliticalViewOption;
    }

    geopoliticalViewValidator(geopoliticalViewOption);
    return geopoliticalViewOption;
}

function initializeRTLTextPlugin(mapbox) {
    const blob = new Blob([mapboxGlRTLText], {type: 'text/javascript'});

    // a similar check is done internally when setting the plugin
    // but it's not exposed outside the method
    // eslint-disable-next-line
    // https://github.com/mapbox/mapbox-gl-js/blob/e0c4d4679d06be7c0f51970b26a184f8d9ed6d5b/src/source/rtl_text_plugin.js
    if (mapbox.RTLTextPluginRequested === true) {
        return;
    }
    mapbox.RTLTextPluginRequested = true;

    mapbox
        .setRTLTextPlugin(URL.createObjectURL(blob),
            (error) => {
                if (error) {
                    throw Error(error);
                }
            });
}

/**
 * @private
 * @class MapConfigurer
 * @constructor
 * @param {Object} options
 * @param {Object} dependencies
 * @param {CoreMap} dependencies.coreMap
 */
export default class MapConfigurer {
    constructor(options, {
        mapbox
    }) {
        this.options = {...options};

        this.options = initializeMapOptions(options);

        this.options.attributionControl = false;
        this.mapbox = mapbox;

        this.options.language = convertAndValidateLanguage(this.options.language);
        this.options.geopoliticalView = convertAndValidateGeopoliticalView(this.options.geopoliticalView);

        this.tiersContainer = tiersContainer();
        this.options.transformRequest = transformRequestFactory(this.tiersContainer);
    }

    /**
     * @method createMap
     * @return {Map} Instance of the map
     */
    createMap = () => {
        const map = mapboxMapFactory({...this.options});
        const sessionIdInstance = sessionId.getInstance();
        sessionIdInstance.initialize(map);
        sessionIdInstance.create();
        const extendedMap = extendedMapFactory(this.tiersContainer, this.options);
        appendMapboxProperties(extendedMap, map);
        initializeCopyrights(this.options.key, this.options.attributionControlPosition, extendedMap);
        initializeRTLTextPlugin(this.mapbox);

        extendedMap._populateTiers().then(() => {
            extendedMap.__om.fire(CUSTOM_EVENTS.MAP_LOAD);
            sourceValidationConfigurer.setUp(extendedMap);
        });

        return extendedMap;
    }
}



// WEBPACK FOOTER //
// ./map/configurer/mapConfigurer.js